import { FORM_ELEMENT_KEYS } from '@app/misc/mobile-view-builder/templates/form-elements';
import { MESSAGES } from '@app/dashboard/fleet-cards/main.constants';

export const AMOUNT_REGEX: RegExp = /^(\d{1,3})+(\,(\d{3}))*\.\d{1,}$/;
export const EMOJI_PATTERN: RegExp = /^[\u{203C}\u{2049}\u{20E3}\u{2122}\u{2139}\u{2194} -\u{2199}\u{21A9} -\u{21AA}\u{231A} -\u{231B}\u{23E9} -\u{23EC}\u{23F0}\u{23F3}\u{24C2}\u{25AA} -\u{25AB}\u{25B6}\u{25C0}\u{25FB} -\u{25FE}\u{2600} -\u{2601}\u{260E}\u{2611}\u{2614} -\u{2615}\u{261D}\u{263A}\u{2648} -\u{2653}\u{2660}\u{2663}\u{2665} -\u{2666}\u{2668}\u{267B}\u{267F}\u{2693}\u{26A0} -\u{26A1}\u{26AA} -\u{26AB}\u{26BD} -\u{26BE}\u{26C4} -\u{26C5}\u{26CE}\u{26D4}\u{26EA}\u{26F2} -\u{26F3}\u{26F5}\u{26FA}\u{26FD}\u{2702}\u{2705}\u{2708} -\u{270C}\u{270F}\u{2712}\u{2714}\u{2716}\u{2728}\u{2733} -\u{2734}\u{2744}\u{2747}\u{274C}\u{274E}\u{2753} -\u{2755}\u{2757}\u{2764}\u{2795} -\u{2797}\u{27A1}\u{27B0}\u{2934} -\u{2935}\u{2B05} -\u{2B07}\u{2B1B} -\u{2B1C}\u{2B50}\u{2B55}\u{3030}\u{303D}\u{3297}\u{3299}\u{1F004}\u{1F0CF}\u{1F170} -\u{1F171}\u{1F17E} -\u{1F17F}\u{1F18E}\u{1F191} -\u{1F19A}\u{1F1E7} -\u{1F1EC}\u{1F1EE} -\u{1F1F0}\u{1F1F3}\u{1F1F5}\u{1F1F7} -\u{1F1FA}\u{1F201} -\u{1F202}\u{1F21A}\u{1F22F}\u{1F232} -\u{1F23A}\u{1F250} -\u{1F251}\u{1F300} -\u{1F320}\u{1F330} -\u{1F335}\u{1F337} -\u{1F37C}\u{1F380} -\u{1F393}\u{1F3A0} -\u{1F3C4}\u{1F3C6} -\u{1F3CA}\u{1F3E0} -\u{1F3F0}\u{1F400} -\u{1F43E}\u{1F440}\u{1F442} -\u{1F4F7}\u{1F4F9} -\u{1F4FC}\u{1F500} -\u{1F507}\u{1F509} -\u{1F53D}\u{1F550} -\u{1F567}\u{1F5FB} -\u{1F640}\u{1F645} -\u{1F64F}\u{1F680} -\u{1F68A}|]*$/i;
export const VALID_SERVER_ENCRYPTED_SSN_LENGTH = 72;

// Backend sends encrypted SSN value in get draft application API call. Since the encrypted string length would always
// be greater than a valid SSN string length, we use a placeholder value to show on the UI.
export const SERVER_ENCRYPTED_SSN_PLACEHOLDER = '***********';

export const NON_PO_BOX_ADDRESS_PLACEHOLDER = $localize`Street address (no PO Boxes)`;
export const SERVER_ERROR_MESSAGES = {
  'invalid_ssn': MESSAGES.CARD_APPLICATION_INVALID_SSN_ERROR,
  'ineligible to apply': MESSAGES.CARD_APPLICATION_INELIGIBLE_TO_APPLY_ERROR,
  'application_declined': MESSAGES.APPLICATION_DECLINED,
};

export const ADDRESS_DETAILS_KEYS = {
  address1: 'address1',
  address2: 'address2',
  city: 'city',
  state: 'state',
  country: 'country',
  postal_code: 'postal_code',
};

export const generateAddressFieldsWithGroup = (groupName: string) => {
  return {
    [ADDRESS_DETAILS_KEYS.address1]: {
      label: $localize`Street Info`,
      placeholder: $localize`Street address`,
      pattern: EMOJI_PATTERN,
      type: FORM_ELEMENT_KEYS.TEXT,
      required: true,
      groupName,
    },
    [ADDRESS_DETAILS_KEYS.address2]: {
      label: $localize`Apartment Info`,
      placeholder: $localize`Apartment, suite, etc. (optional)`,
      pattern: EMOJI_PATTERN,
      type: FORM_ELEMENT_KEYS.TEXT,
      required: false,
      groupName,
    },
    [ADDRESS_DETAILS_KEYS.city]: {
      label: $localize`City`,
      placeholder: $localize`City`,
      pattern: EMOJI_PATTERN,
      type: FORM_ELEMENT_KEYS.TEXT,
      required: true,
      groupName,
    },
    [ADDRESS_DETAILS_KEYS.state]: {
      label: $localize`State`,
      placeholder: $localize`State`,
      pattern: EMOJI_PATTERN,
      type: FORM_ELEMENT_KEYS.TEXT,
      required: true,
      groupName,
    },
    [ADDRESS_DETAILS_KEYS.country]: {
      label: $localize`Country`,
      placeholder: $localize`Country`,
      pattern: EMOJI_PATTERN,
      type: FORM_ELEMENT_KEYS.TEXT,
      required: true,
      groupName,
    },
    [ADDRESS_DETAILS_KEYS.postal_code]: {
      label: $localize`ZIP code`,
      placeholder: $localize`ZIP code`,
      type: FORM_ELEMENT_KEYS.TEXT,
      pattern: '^[0-9]{5}(-?[0-9]{4}){0,1}$',
      required: true,
      maxLength: 10,
      minLength: 5,
      groupName,
    },
  };
};

export const INTRO_QUESTION_FORM_KEYS = {
  number_of_cards: 'number_of_cards',
  number_of_vehicles: 'number_of_vehicles',
  spend_requested_in_dollars: 'spend_requested_in_dollars',
};

export const INTRO_QUESTION_FORM_CONFIG = {
  [INTRO_QUESTION_FORM_KEYS.number_of_cards]: {
    label: $localize`How many cards do you need?`,
    placeholder: $localize`Please specify`,
    type: FORM_ELEMENT_KEYS.NUMBER,
    required: true,
    minValue: 1,
  },
  [INTRO_QUESTION_FORM_KEYS.number_of_vehicles]: {
    label: $localize`How many vehicles do you have in your fleet?`,
    placeholder: $localize`Please specify`,
    type: FORM_ELEMENT_KEYS.NUMBER,
    required: true,
    maxLength: 5,
    minValue: 1,
  },
  [INTRO_QUESTION_FORM_KEYS.spend_requested_in_dollars]: {
    label: $localize`How much does your company spend per week?`,
    placeholder: '',
    type: FORM_ELEMENT_KEYS.NUMBER,
    required: true,
    minValue: 1,
  },
};

export const BILLING_TIMEZONE_KEY = {
  billing_timezone: 'billing_timezone',
};

export const INCORPORATION_DETAILS_KEYS = {
  incorporation_type: 'incorporation_type',
  state_of_incorporation: 'state_of_incorporation',
};

export const INCORPORATION_DETAILS_CONFIG = {
  [INCORPORATION_DETAILS_KEYS.state_of_incorporation]: {
    label: $localize`State`,
    placeholder: $localize`State`,
    pattern: EMOJI_PATTERN,
    type: FORM_ELEMENT_KEYS.TEXT,
    required: true,
    groupName: 'incorporation',
  },
  [INCORPORATION_DETAILS_KEYS.incorporation_type]: {
    label: $localize`Type of business`,
    placeholder: $localize`Select`,
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    options: [
      {
        key: 'LLC',
        label: $localize`LLC`,
      },
      {
        key: 'PARTNERSHIP',
        label: $localize`Partnership`,
      },
      {
        key: 'CORPORATION',
        label: $localize`Corporation`,
      },
      {
        key: 'COOPERATIVE',
        label: $localize`Co-operative`,
      },
      {
        key: 'SOLE_PROPRIETORSHIP',
        label: $localize`Sole Proprietorship`,
      },
    ],
    required: true,
    groupName: 'incorporation',
  },
};

export const BILLING_TIMEZONE_CONFIG = {
  [BILLING_TIMEZONE_KEY.billing_timezone]: {
    label: $localize`Time zone`,
    placeholder: $localize`Please select`,
    requestKey: 'billing_timezone',
    responseKey: 'billing_timezone',
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    required: true,
  },
};

export const BUSINESS_DETAILS_FORM_KEYS = {
  business_name_legal: 'business_name_legal',
  business_name_dba: 'business_name_dba',
  industry_type: 'industry_type',
  business_ein: 'business_ein',
  date_established: 'date_established',
  shipping_preference: 'shipping_preference',
  annual_revenue_in_dollars: 'annual_revenue_in_dollars',
  ...INCORPORATION_DETAILS_KEYS,
  ...ADDRESS_DETAILS_KEYS,
};

export const PUBLIC_CARD_APP_BUSINESS_DETAILS_FORM_KEYS = {
  dot_id: 'dot_id',
  number_of_vehicles: 'number_of_vehicles',
  ...BUSINESS_DETAILS_FORM_KEYS,
};

export const BUSINESS_DETAILS_FORM_CONFIG = {
  [BUSINESS_DETAILS_FORM_KEYS.business_name_legal]: {
    label: $localize`Legal Business name`,
    placeholder: $localize`Please specify`,
    pattern: '^[A-Za-z0-9,&\/_\.\' \-]*$',
    type: FORM_ELEMENT_KEYS.TEXT,
    required: true,
  },
  [BUSINESS_DETAILS_FORM_KEYS.business_name_dba]: {
    label: $localize`Doing Business As (DBA) (optional)`,
    pattern: EMOJI_PATTERN,
    placeholder: $localize`(21 characters max)`,
    type: FORM_ELEMENT_KEYS.TEXT,
    maxLength: 21,
  },
  [BUSINESS_DETAILS_FORM_KEYS.industry_type]: {
    label: $localize`Industry Type`,
    placeholder: $localize`select`,
    pattern: '^[^@$*)([#+\"|<>{!]+$',
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    options: [
      {
        key: 'AGRICULTURE',
        label: $localize`Agriculture`,
      },
      {
        key: 'EDUCATION',
        label: $localize`Education`,
      },
      {
        key: 'ELECTRIC_&_WATER',
        label: $localize`Electric & Water`,
      },
      {
        key: 'ELECTRICAL_PLUMBING_MECHANICAL',
        label: $localize`Electrical, Plumbing, Mechanical`,
      },
      {
        key: 'FINANCE_INSURANCE_&_REALESTATE',
        label: $localize`Finance, Insurance & Real Estate`,
      },
      {
        key: 'FINISH_CONTRACTORS',
        label: $localize`Finish Contractors`,
      },
      {
        key: 'FOOD_&_BEVERAGE',
        label: $localize`Food & Beverage`,
      },
      {
        key: 'GOVERNMENT_&_PUBLIC_SAFETY',
        label: $localize`Government & Public Safety`,
      },
      {
        key: 'HEALTHCARE_&_PHARMA',
        label: $localize`Healthcare & Pharma`,
      },
      {
        key: 'HEAVY_CONSTRUCTION',
        label: $localize`Heavy Construction`,
      },
      {
        key: 'MANUFACTURING',
        label: $localize`Manufacturing`,
      },
      {
        key: 'MARINE_AIR_&_RAIL_TRANSPORTATION',
        label: $localize`Marine, Air & Rail Transportation`,
      },
      {
        key: 'MINING',
        label: $localize`Mining`,
      },
      {
        key: 'MISC_HOME_&_BUSINESS_SERVICES',
        label: $localize`Misc. Home & Business Services`,
      },
      {
        key: 'NON_HEAVY_CONSTRUCTION',
        label: $localize`Non-Heavy Construction`,
      },
      {
        key: 'OIL_&_GAS',
        label: $localize`Oil & Gas`,
      },
      {
        key: 'OTHER',
        label: $localize`Other`,
      },
      {
        key: 'PASSENGER_TRANSPORT',
        label: $localize`Passenger Transport`,
      },
      {
        key: 'RETAIL_TRADE',
        label: $localize`Retail Trade`,
      },
      {
        key: 'TELECOMMUNICATIONS',
        label: $localize`Telecommunications`,
      },
      {
        key: 'TRUCKING_&_WAREHOUSING',
        label: $localize`Trucking & Warehousing`,
      },
      {
        key: 'VEHICLE_RENTALS_&_DEALERS',
        label: $localize`Vehicle Rentals & Dealers`,
      },
      {
        key: 'VEHICAL_REPAIR_&_OTHER_SERVICES',
        label: $localize`Vehicle Repair & Other Services`,
      },
      {
        key: 'WASTE_&_RECYCLING',
        label: $localize`Waste and Recycling`,
      },
      {
        key: 'WHOLESALE_TRADE',
        label: $localize`Wholesale Trade`,
      },
    ],
    required: false,
  },
  [BUSINESS_DETAILS_FORM_KEYS.business_ein]: {
    label: $localize`Tax or Employer ID Number (TIN/EIN)`,
    placeholder: '__-_______',
    type: FORM_ELEMENT_KEYS.TEXT,
    pattern: '^[0-9]{2}-?[0-9]{7}$',
    required: true,
  },
  [BUSINESS_DETAILS_FORM_KEYS.date_established]: {
    label: $localize`Year`,
    placeholder: $localize`Year`,
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    required: true,
  },
  [BUSINESS_DETAILS_FORM_KEYS.shipping_preference]: {
    label: $localize`Shipping method`,
    placeholder: $localize`Shipping method`,
    type: FORM_ELEMENT_KEYS.SINGLE_SELECT,
    required: true,
  },
  [BUSINESS_DETAILS_FORM_KEYS.annual_revenue_in_dollars]: {
    label: $localize`What was your total annual revenue last year?`,
    placeholder: '',
    type: FORM_ELEMENT_KEYS.CURRENCY,
    minValue: 1,
  },
  ...INCORPORATION_DETAILS_CONFIG,
  ...generateAddressFieldsWithGroup('office_location'),
};

export const PUBLIC_CARD_APP_BUSINESS_DETAILS_FORM_CONFIG = {
  [PUBLIC_CARD_APP_BUSINESS_DETAILS_FORM_KEYS.dot_id]: {
    label: $localize`DOT number (optional)`,
    placeholder: $localize`Please specify`,
    pattern: EMOJI_PATTERN,
    type: FORM_ELEMENT_KEYS.TEXT,
  },
  [PUBLIC_CARD_APP_BUSINESS_DETAILS_FORM_KEYS.number_of_vehicles]: {
    label: $localize`How many vehicles do you have in your fleet?`,
    placeholder: $localize`Please specify`,
    type: FORM_ELEMENT_KEYS.NUMBER,
    required: true,
    maxLength: 5,
    minValue: 1,
  },
  ...BUSINESS_DETAILS_FORM_CONFIG,
};

export const ATTEST_FORM_KEYS = {
  first_name: 'first_name',
  last_name: 'last_name',
  job_title: 'job_title',
  consent: 'consent',
  partner_bridgehaul: 'partner_bridgehaul',
};

export const ATTEST_FORM_CONFIG = {
  [ATTEST_FORM_KEYS.first_name]: {
    label: $localize`First name`,
    placeholder: $localize`First name`,
    type: FORM_ELEMENT_KEYS.TEXT,
    required: true,
    minLength: 2,
    pattern: '^[A-Za-z]*$',
  },
  [ATTEST_FORM_KEYS.last_name]: {
    label: $localize`Last name`,
    placeholder: $localize`Last name`,
    type: FORM_ELEMENT_KEYS.TEXT,
    required: true,
    minLength: 2,
    pattern: '^[A-Za-z]*$',
  },
  [ATTEST_FORM_KEYS.job_title]: {
    label: $localize`Job title`,
    placeholder: $localize`Please Specify`,
    type: FORM_ELEMENT_KEYS.TEXT,
    required: true,
    pattern: EMOJI_PATTERN,
  },
  [ATTEST_FORM_KEYS.consent]: {
    label: $localize`Consent`,
    placeholder: $localize`Consent`,
    type: FORM_ELEMENT_KEYS.SIGNATURE,
    required: true,
  },
  [ATTEST_FORM_KEYS.partner_bridgehaul]: {
    label: $localize`Partner Consent BridgeHaul`,
    placeholder: $localize`Partner Consent BridgeHaul`,
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
};

export const APPLICATION_PREFERENCES_FORM_KEY = 'notification_preferences';

export const APPLICATION_PREFERENCES_FORM_KEYS = {
  credit_approval_email_receipients: 'credit_approval_email_receipients',
};

export const APPLICATION_PREFERENCES_FORM_CONFIG = {
  [APPLICATION_PREFERENCES_FORM_KEYS.credit_approval_email_receipients]: {
    label: $localize`Recipients (optional)`,
    placeholder: 'name1@email.com, name2@email.com',
    required: false,
    type: FORM_ELEMENT_KEYS.TEXT,
  },
};

export const BUSINESS_OWNER_DETAILS_KEYS = {
  first_name: 'first_name',
  last_name: 'last_name',
  dob: 'dob',
  ssn: 'ssn',
  ...ADDRESS_DETAILS_KEYS,
};

export const BUSINESS_OWNER_DETAILS_CONFIG = {
  [BUSINESS_OWNER_DETAILS_KEYS.first_name]: {
    label: $localize`First Name`,
    placeholder: $localize`First Name`,
    requestKey: 'first_name',
    responseKey: 'first_name',
    type: FORM_ELEMENT_KEYS.TEXT,
    required: true,
    minLength: 2,
    pattern: '^[A-Za-z]*$',
  },
  [BUSINESS_OWNER_DETAILS_KEYS.last_name]: {
    label: $localize`Last Name`,
    placeholder: $localize`Last Name`,
    type: FORM_ELEMENT_KEYS.TEXT,
    required: true,
    minLength: 2,
    pattern: '^[A-Za-z]*$',
  },
  [BUSINESS_OWNER_DETAILS_KEYS.dob]: {
    label: $localize`Date of birth`,
    placeholder: $localize`MM-DD-YYYY`,
    type: FORM_ELEMENT_KEYS.DATE_TIME,
    required: true,
  },
  [BUSINESS_OWNER_DETAILS_KEYS.ssn]: {
    label: $localize`Social Security Number (SSN)`,
    placeholder: '___-__-____',
    type: FORM_ELEMENT_KEYS.TEXT,
    pattern: '^(?!000|666)[0-8][0-9]{2}-?(?!00)[0-9]{2}-?(?!0000)[0-9]{4}$',
    required: true,
  },
  ...generateAddressFieldsWithGroup('home'),
};

export const EXECUTIVE_OWNER_DETAILS_KEYS = {
  ...BUSINESS_OWNER_DETAILS_KEYS,
  is_beneficial_owner: 'is_beneficial_owner',
};

export const EXECUTIVE_OWNER_DETAILS_CONFIG = {
  ...BUSINESS_OWNER_DETAILS_CONFIG,
  [EXECUTIVE_OWNER_DETAILS_KEYS.is_beneficial_owner]: {
    label: $localize`This person is also a business owner`,
    placeholder: $localize`This person is also a business owner`,
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
};

export const SHIPPING_DETAILS_KEYS = {
  ...ADDRESS_DETAILS_KEYS,
  is_same_as_business_address: 'is_same_as_business_address',
};

export const SHIPPING_DETAILS_CONFIG = {
  [SHIPPING_DETAILS_KEYS.is_same_as_business_address]: {
    label: $localize`Same as business address`,
    placeholder: $localize`Same as business address`,
    type: FORM_ELEMENT_KEYS.TOGGLE,
    required: false,
  },
  ...generateAddressFieldsWithGroup('shipping_location'),
};

export const SHIPPING_OPTIONS = {
  [$localize`Standard shipping (7-10 business days)`]: 'standard',
  [$localize`Express shipping (3-5 business days)`]: 'express',
};

export enum SHIPPING_TYPES {
  STANDARD = 'standard',
  EXPRESS = 'express',
}

export enum ASSIGN_PHONE_MODEL_TYPES {
  SECURITY = 'security',
  REPORTING = 'reporting',
}

export const TIME_ZONES_LIST = [
  {
    label: $localize`Eastern Time (US & Canada)`,
    value: 'Eastern Time (US & Canada)',
  },
  {
    label: $localize`Indiana (East)`,
    value: 'Indiana (East)',
  },
  {
    label: $localize`Central Time (US & Canada)`,
    value: 'Central Time (US & Canada)',
  },
  {
    label: $localize`Saskatchewan`,
    value: 'Saskatchewan',
  },
  {
    label: $localize`Mountain Time (US & Canada)`,
    value: 'Mountain Time (US & Canada)',
  },
  {
    label: $localize`Arizona`,
    value: 'Arizona',
  },
  {
    label: $localize`Pacific Time (US & Canada)`,
    value: 'Pacific Time (US & Canada)',
  },
  {
    label: $localize`Alaska`,
    value: 'Alaska',
  },
  {
    label: $localize`Atlantic Time (Canada)`,
    value: 'Atlantic Time (Canada)',
  },
  {
    label: $localize`Newfoundland`,
    value: 'Newfoundland',
  },
  {
    label: $localize`Hawaii`,
    value: 'Hawaii',
  },
];
